import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { SignagePlaylistPreview } from "@shared/types/signage";
import { foyerApi } from "../http/foyer";
import { tokens } from "../theme";
import SignagePreviewPlayer from "./SignagePreviewPlayer";

/**
 * Watch a channel before scheduling it.
 *
 * ## What it plays, and what it does not
 *
 * The FIRST clip of the loop, and only that. The question this answers is "is
 * this the channel I think it is" - a glance before you put it on a wall - not
 * "what does this channel do all afternoon". Resolving 355 catalogue ids to
 * answer the first question would be an absurd amount of work for a click, and
 * playing the loop through would be a second, worse, copy of the TV app.
 *
 * The player itself is `SignagePreviewPlayer`, shared with the item-level
 * preview in `SignagePlaylistDialog` - see that file for why it is not
 * written twice.
 */
export default function SignageChannelPreviewDialog({
  channelId,
  channelName,
  onClose,
}: {
  /** `null` closes the dialog. */
  channelId: string | null;
  channelName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation("signage");
  const [preview, setPreview] = useState<SignagePlaylistPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = channelId !== null;

  useEffect(() => {
    if (open) return;
    setPreview(null);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!channelId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    foyerApi
      .signagePlaylistPreview(channelId)
      .then((p) => alive && setPreview(p))
      .catch(
        () => alive && setError(t("signageChannelPreviewDialog.loadFailed"))
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [channelId, t]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      // A lightbox, so the frame gets out of the way of the picture.
      PaperProps={{ sx: { bgcolor: tokens.surface } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, pt: 2.5, pb: 1.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              typography: "title",
              fontWeight: "fontWeightBold",
              color: tokens.ink,
            }}
          >
            {channelName}
          </Typography>
          {/* Says what is on screen. Without it, a one-clip glance reads as
              "this channel is 20 seconds long". */}
          <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
            {t("signageChannelPreviewDialog.firstClipNote")}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label={t("signageChannelPreviewDialog.closePreview")}
        >
          <CloseIcon />
        </IconButton>
      </Stack>
      <DialogContent sx={{ pt: 0, pb: 3 }}>
        <SignagePreviewPlayer
          preview={preview}
          loading={loading}
          error={error}
          emptyText={t("signageChannelPreviewDialog.emptyChannel")}
        />
      </DialogContent>
    </Dialog>
  );
}
